import {
  EmptyState,
  Field,
  FilterSegments,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  formatDate,
  formatRelative,
} from '@westhetics/ui'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { PERIODS, useClinics, useSavePeriod, type Period } from './api'
import { PERIOD_LABELS, money } from './labels'

/**
 * A lista de clínicas do root sem clínica ativa (docs/areas/dashboard.md, RN-DSH-23; P-DSH-11): uma
 * linha por clínica com pendências, valor gerado, faturamento real e o último atendimento, ordenada
 * pelas críticas. Sem soma global. A linha troca a clínica ativa e abre o dashboard dela.
 */
export function ClinicsList() {
  const session = useSession()
  const navigate = useNavigate()
  const [period, setPeriod] = useState<Period | undefined>(undefined)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounced(search.trim(), 300)
  const query = useClinics({ period, search: debouncedSearch || undefined, page })
  const savePeriod = useSavePeriod()
  const data = query.data
  const active = period ?? data?.meta.period.period ?? 'week'

  function changePeriod(next: Period) {
    setPeriod(next)
    setPage(1)
    savePeriod.mutate(next)
  }

  async function open(id: string) {
    await session.switchClinic(id)
    navigate('/dashboard')
  }

  return (
    <section aria-label="Clínicas" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FilterSegments
          label="Período"
          value={active}
          onChange={changePeriod}
          options={PERIODS.map((p) => ({ value: p, label: PERIOD_LABELS[p] }))}
        />
        <Field label="Buscar" optional className="min-w-[240px]">
          <Input
            type="search"
            placeholder="nome ou CNPJ"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
          />
        </Field>
      </div>

      {query.isError ? (
        <LoadError
          title="Não conseguimos carregar as clínicas"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : !data ? (
        <TableSkeleton rows={4} columns={5} />
      ) : data.data.length === 0 ? (
        <EmptyState
          title="Nenhuma clínica ativa"
          description={
            debouncedSearch
              ? 'Nenhuma clínica com esse termo. A busca cobre nome e CNPJ.'
              : 'As clínicas ativas aparecem aqui com as pendências e o resultado do período.'
          }
        />
      ) : (
        <div>
          <Table>
            <TableHeader>
              <tr>
                <TableHead>Clínica</TableHead>
                <TableHead>Pendências</TableHead>
                <TableHead>Valor gerado</TableHead>
                <TableHead>Faturamento real</TableHead>
                <TableHead>Último atendimento</TableHead>
              </tr>
            </TableHeader>
            <TableBody>
              {data.data.map((row) => (
                <TableRow
                  key={row.id}
                  critical={row.pendingItems.critical > 0}
                  onClick={() => void open(row.id)}
                >
                  <TableCell label="Clínica">
                    {row.name}
                    <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5]">
                      {row.city}/{row.state}
                    </div>
                  </TableCell>
                  <TableCell label="Pendências">
                    {row.pendingItems.total === 0 ? (
                      <span className="text-secondary">nenhuma</span>
                    ) : (
                      <span className={row.pendingItems.critical > 0 ? 'text-plum' : undefined}>
                        {row.pendingItems.total}
                        {row.pendingItems.critical > 0
                          ? ` · ${row.pendingItems.critical} crítica${row.pendingItems.critical === 1 ? '' : 's'}`
                          : ''}
                      </span>
                    )}
                  </TableCell>
                  <TableCell label="Valor gerado">
                    {money(row.generated.amountCents)}
                    <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5]">
                      {row.generated.count}{' '}
                      {row.generated.count === 1 ? 'atendimento' : 'atendimentos'}
                    </div>
                  </TableCell>
                  <TableCell label="Faturamento real" muted>
                    {money(row.netRevenueCents)}
                  </TableCell>
                  <TableCell label="Último atendimento" muted>
                    {row.lastAppointmentDate
                      ? formatDate(new Date(`${row.lastAppointmentDate}T12:00:00`))
                      : 'nenhum'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ListFooter
            meta={data.meta}
            noun={['clínica', 'clínicas']}
            note={`${data.meta.period.label} · calculado ${formatRelative(new Date(data.meta.calculatedAt))} · clique numa linha para abrir o dashboard da clínica`}
            onPage={setPage}
          />
        </div>
      )}
    </section>
  )
}
